<script setup lang="ts">
import type { CharacteristicItem } from '~/components/CharacteristicsGrid';
import type { Tab } from '~/components/TabsNavigator';
import type {
	BreadcrumbItem,
	BreadcrumbItemWithIndex
} from '~/components/ui/breadcrumb';
import type { Agent } from '~/core/entities/agent/Agent';
import type { PropertyTypeObject } from '~/core/entities/CRMEnums';
import type { Property } from '~/core/entities/property';
import type { LocationClickedEvent, SectionName } from '~/types/mixpanel';

import { useFeatureFlags } from '~/composables/useFeatureFlags';
import { useFloatingCta } from '~/composables/useFloatingCta';
import { usePropertyContextualMenu } from '~/composables/usePropertyContextualMenu';
import { ORDER_BY } from '~/core/entities/property/filters';
import GmapClusterRadius from '~/core/service/googleMaps/components/GmapClusterRadius/GmapClusterRadius.vue';
import { useNavigationStore } from '~/store/navigationStore';
import { getScrollTopForElement } from '~/utils/fixedHeaderOffset';
import { getLocationSlugFromProperty } from '~~/utils';

import { SECTION_TYPES } from '~/components/ContextualHeader';
import { CopyButton } from '~/components/CopyButton';
import PropertyHeroSection from '~/components/PropertyHeroSection/HeroSection.vue';
import PropertyHeroSectionDeprecated from '~/components/PropertyHeroSectionDeprecated/HeroSection.vue';

import IconMaterialSymbolsLightCorporateFare from '~icons/material-symbols-light/corporate-fare';
import IconMaterialSymbolsLightVillaOutline from '~icons/material-symbols-light/villa-outline';

const props = defineProps<{
	property: Property | null;
	isFetchingProperty: boolean;
	isFetchingSimilarProperties: boolean;
	isFetchingChosenForYouProperties: boolean;
	similarProperties: Property[] | null | undefined;
	chosenForYouProperties: Property[] | null | undefined;
	launchYear: string;
	characteristics: CharacteristicItem[];
	breadcrumbItems: BreadcrumbItem[];
	shouldShowCharacteristics: boolean;
	shouldShowFeaturesSection: boolean;
	shouldShowHighlightedImagesSection: boolean;
	similarPropertiesHref: string | null;
	chosenForYouCtaLink: string;
	centerCoordinates: { lat: number; lng: number };
	nearPropertiesLink: string;
	shouldShowFixedFooterExclusiveTag: boolean;
	shouldShowMobileFixedFooterExclusiveTag: boolean;
	shouldShowSimilarPropertyCTA: boolean;
	heroCtaText: string;
	isPropertyModalOpen: boolean;
	isContactModalOpen: boolean;
	currentPropertyModalTab: Tab;
	isCarouselOpen: boolean;
	initialPhotoIndex: number;
}>();

const emit = defineEmits<{
	(e: 'photo-clicked', photoIndex: number): void;
	(
		e: 'photo-modal-opened',
		sectionName: SectionName,
		clickType: 'photo' | 'cta',
		photoIndex: number,
		photoUrl?: string,
		ctaText?: string
	): void;
	(
		e: 'video-modal-opened',
		clickType: 'video' | 'cta',
		videoUrl?: string
	): void;
	(e: 'similar-clicked'): void;
	(
		e: 'map-modal-opened',
		sectionName: SectionName,
		ctaText: string,
		clickEvent?: LocationClickedEvent
	): void;
	(
		e: 'form-property-clicked',
		params: {
			sectionName: string;
			ctaText: string;
			companyName: string;
			agentName: string;
			askingPrice: number | null;
			openModal?: boolean;
		}
	): void;
	(e: 'breadcrumb-clicked', event: BreadcrumbItemWithIndex): void;
	(
		e: 'view-more-clicked',
		sectionName: SectionName,
		destination: string,
		ctaText: string
	): void;
	(e: 'near-properties-clicked', destinationUrl: string): void;
	(e: 'scroll-to-characteristics'): void;
	(e: 'copy-commercial-id'): void;
	(e: 'toggle-contact-modal'): void;
	(e: 'scroll-to-similar-properties', ctaType: 'floating' | 'fixed'): void;
	(e: 'open-contact-modal'): void;
	(e: 'update:shouldShowFixedFooterExclusiveTag', value: boolean): void;
	(e: 'update:shouldShowStickyPulse', value: boolean): void;
	(e: 'update:isPropertyModalOpen', value: boolean): void;
	(e: 'update:currentPropertyModalTab', value: Tab): void;
	(e: 'update:isCarouselOpen', value: boolean): void;
	(e: 'update:isContactModalOpen', value: boolean): void;
	(e: 'click-photo', payload: { photoCount: number; photoUrl: string }): void;
	(e: 'click-video', videoUrl: string): void;
	(e: 'click-fixed-contact-button'): void;
	(e: 'update:has-shorts-videos', value: boolean): void;
}>();

const navigationStore = useNavigationStore();
const { ft_shorts_access_points, ft_shorts_pdp_hero_section } =
	useFeatureFlags();
const propertyHeroSectionComponent = computed(() =>
	ft_shorts_pdp_hero_section
		? PropertyHeroSection
		: PropertyHeroSectionDeprecated
);

const shortsSectionElRef = ref<HTMLElement | null>(null);
const shortsHasVideos = ref(false);

// Use the contextual menu composable to set up header visibility control
const {
	characteristicsRef,
	overviewRef,
	locationRef,
	contactRef,
	chosenForYouRef,
	heroSectionRef,
	propertyPricesRef
} = usePropertyContextualMenu({
	shortsSectionRef: shortsSectionElRef,
	hasShorts: shortsHasVideos
});

// Get the hero section HTMLElement for the floating CTA observer
const heroSectionEl = computed(() => heroSectionRef.value?.$el as HTMLElement);

const highlightedRegionPropertiesDetailsSectionRef = ref<HTMLElement | null>(
	null
);

const {
	registerSection,
	targetRef: floatingCtaTargetRef,
	shouldShowFloatingCta
} = useFloatingCta({
	heroSectionRef: heroSectionEl
});

// Sections with external refs (shared with contextual menu or other logic)
registerSection('chosenForYou', { source: chosenForYouRef });
registerSection('similar', {
	source: highlightedRegionPropertiesDetailsSectionRef
});

const shortsSectionRef = registerSection('shorts');

function handleShortsHasVideos(value: boolean): void {
	shortsHasVideos.value = value;
	emit('update:has-shorts-videos', value);
}

watch(
	[shortsSectionElRef, shortsHasVideos],
	([el, hasVideos]) => {
		shortsSectionRef.value = hasVideos ? el : null;
	},
	{ immediate: true }
);

const shortsTitle = computed(
	() => `Explore ${props.property?.region || ''} em vídeos`
);

const shortsFilters = computed(() => ({
	referenceProperty: props.property?.commercialId || '',
	orderBy: ORDER_BY.DESC
}));

const shouldShowFloatingCTA = computed<boolean>(
	() =>
		(props.shouldShowSimilarPropertyCTA || shortsHasVideos.value) &&
		shouldShowFloatingCta.value
);

const regionClicked = computed<string>(() => {
	return `${props.property?.region}, ${props.property?.city} - ${props.property?.state}`;
});

const isPropertyModalOpenModel = computed({
	get: () => props.isPropertyModalOpen,
	set: value => emit('update:isPropertyModalOpen', value)
});

const currentPropertyModalTabModel = computed({
	get: () => props.currentPropertyModalTab,
	set: value => emit('update:currentPropertyModalTab', value)
});

const isCarouselOpenModel = computed({
	get: () => props.isCarouselOpen,
	set: value => emit('update:isCarouselOpen', value)
});

const isContactModalOpenModel = computed({
	get: () => props.isContactModalOpen,
	set: value => emit('update:isContactModalOpen', value)
});

function handleLocationClicked(event: LocationClickedEvent) {
	emit('map-modal-opened', 'overview', regionClicked.value, event);
}

function scrollToSection(ref: HTMLElement | { $el: HTMLElement } | null) {
	if (ref) {
		const element = ref instanceof HTMLElement ? ref : ref.$el;

		scrollTo({
			top: getScrollTopForElement(element, 12),
			behavior: 'smooth'
		});
	}
}

function scrollToCharacteristics() {
	emit('scroll-to-characteristics');
	scrollToSection(characteristicsRef.value);
}

function handleFloatingCtaClick() {
	scrollToSection(floatingCtaTargetRef.value);
	emit('scroll-to-similar-properties', 'floating');
}

defineExpose({
	highlightedRegionPropertiesDetailsSectionRef,
	/** Chosen For You section element; used as scroll target fallback */
	floatingCtaChosenForYouRef: chosenForYouRef
});
</script>

<template>
	<div class="flex flex-col bg-white">
		<component
			:is="propertyHeroSectionComponent"
			ref="heroSectionRef"
			:property="property"
			:is-loading="isFetchingProperty"
			:launch-year="launchYear"
			:should-show-similar-property-cta="shouldShowSimilarPropertyCTA"
			@photo-clicked="(photoIndex: number) => emit('photo-clicked', photoIndex)"
			@photo-modal-opened="
				(
					sectionName: SectionName,
					clickType: 'photo' | 'cta',
					photoIndex: number,
					photoUrl?: string,
					ctaText?: string
				) =>
					emit(
						'photo-modal-opened',
						sectionName,
						clickType,
						photoIndex,
						photoUrl,
						ctaText
					)
			"
			@video-modal-opened="
				(clickType: 'video' | 'cta', videoUrl?: string) =>
					emit('video-modal-opened', clickType, videoUrl)
			"
			@similar-clicked="emit('similar-clicked')"
			@map-modal-opened="
				(
					sectionName: SectionName,
					ctaText: string,
					clickEvent?: LocationClickedEvent
				) => emit('map-modal-opened', sectionName, ctaText, clickEvent)
			"
			@form-property-clicked="
				(params: {
					sectionName: string;
					ctaText: string;
					companyName: string;
					agentName: string;
					askingPrice: number | null;
					openModal: boolean;
				}) => emit('form-property-clicked', params)
			"
		/>
		<LazyPropertyModal
			v-if="property"
			v-model="isPropertyModalOpenModel"
			v-model:tab="currentPropertyModalTabModel"
			v-model:is-carousel-open="isCarouselOpenModel"
			:property="property"
			:launch-year="launchYear"
			:initial-photo-index="initialPhotoIndex"
			@click-photo="
				({ photoCount, photoUrl }: { photoCount: number; photoUrl: string }) =>
					emit(
						'photo-modal-opened',
						'photos modal',
						'photo',
						photoCount - 1,
						photoUrl
					)
			"
			@click-video="(videoUrl: string) => emit('click-video', videoUrl)"
			@click-fixed-contact-button="
				emit('form-property-clicked', {
					sectionName: 'photos modal',
					ctaText: heroCtaText,
					companyName: property?.agent?.name || '',
					agentName: property?.company?.name || '',
					askingPrice: property?.askingPrice || null,
					openModal: true
				})
			"
		/>

		<LazyContactFormModal
			v-model="isContactModalOpenModel"
			context="property"
			:broker="property?.agent"
			:company-name="property?.company?.name"
			:company-logo="property?.company?.smallLogo"
			:property-commercial-id="property?.commercialId"
			:property-region="property?.region"
			:property-type-name="property?.propertyType?.name"
			:property-id="property?.id"
			:asking-price="property?.askingPrice"
			:property-city="property?.city"
			:property-state="property?.state"
			:video-shorts="(property?.featuredVideos?.length ?? 0) > 0"
			:location-slug="getLocationSlugFromProperty(property)"
			@scroll-to-similar-properties="
				emit('scroll-to-similar-properties', 'fixed')
			"
		/>

		<section class="w-full">
			<div class="w-full">
				<!-- Mobile only: hydrate on mobile media query -->
				<LazyMediaQueryFixedFooterContact
					v-if="property && !isPropertyModalOpen"
					hydrate-on-media-query="(max-width: 1023px)"
					:agent="property.agent"
					:company-logo="property.company.smallLogo"
					:company-name="property.company.name"
					:is-exclusive="shouldShowMobileFixedFooterExclusiveTag"
					:property-id="property?.id"
					:commercial-id="property?.commercialId"
					:in-user-wishlist="property?.inUserWishlist"
					:video-shorts="(property?.featuredVideos?.length ?? 0) > 0"
					@click-fixed-footer-contact="
						emit('form-property-clicked', {
							sectionName: 'mobile fixed cta',
							ctaText: heroCtaText,
							companyName: property.company.name,
							agentName: property.agent.name,
							askingPrice: property.askingPrice,
							openModal: true
						})
					"
				/>
				<Container
					class="flex flex-col divide-y divide-white-dark-3 p-0! md:px-5! lg:px-12! 2xl:px-32! 3xl:px-36!"
				>
					<div class="flex size-full flex-col-reverse lg:flex-row lg:gap-8">
						<div class="flex flex-col lg:w-[63%] xl:w-[67%] 2xl:w-[67.5%]">
							<div
								:id="SECTION_TYPES.OVERVIEW"
								ref="overviewRef"
								data-section-name="Visão geral"
							>
								<LazySectionsPropertyHighlightedInfosSection
									price-classes="text-primary"
									:property-type="property?.propertyType?.name || ''"
									:region="property?.region || ''"
									:city="property?.city || ''"
									:state="property?.state || ''"
									:is-exclusive="property?.isExclusive"
									@location-clicked="handleLocationClicked"
									@show-footer-exclusive-tag="
										emit('update:shouldShowFixedFooterExclusiveTag', true)
									"
									@hide-footer-exclusive-tag="
										emit('update:shouldShowFixedFooterExclusiveTag', false)
									"
									@show-sticky-pulse="
										emit('update:shouldShowStickyPulse', true)
									"
									@hide-sticky-pulse="
										emit('update:shouldShowStickyPulse', false)
									"
								/>
							</div>
							<div
								class="flex w-full flex-col items-center justify-center divide-y divide-white-dark-3 text-primary sm:items-start sm:justify-start"
							>
								<div
									class="flex w-full flex-col items-start gap-3 px-5 pb-10 md:gap-5 md:px-0 md:pb-12 xl:gap-6"
								>
									<LazyCharacteristicsGrid
										:is-loading="isFetchingProperty"
										:characteristics="characteristics"
									/>

									<!-- Mobile only: hydrate on mobile media query -->
									<LazyMediaQueryCondominiumInfo
										v-if="
											property?.canonicalCondo?.name &&
											property?.canonicalCondo?.slug
										"
										hydrate-on-media-query="(max-width: 767px)"
										title="Condomínio"
										:name="property.canonicalCondo.name"
										:link-to="`/condominio/${property.canonicalCondo.slug}`"
										class="md:hidden"
									>
										<template #icon>
											<IconMaterialSymbolsLightCorporateFare class="size-5" />
										</template>
									</LazyMediaQueryCondominiumInfo>

									<Button
										v-if="shouldShowCharacteristics"
										variant="tertiary"
										class="flex h-10 items-center justify-center gap-2 self-stretch rounded-[2px] border-none px-3 py-2 md:h-11 md:px-4 lg:gap-3 lg:px-5"
										:is-client-only="false"
										skeleton-classes="w-full md:w-auto"
										@click="scrollToCharacteristics"
									>
										<IconMaterialSymbolsLightList class="size-5 text-primary" />
										<span
											class="text-base font-medium leading-[26px] text-primary"
										>
											Ver todas as características
										</span>
									</Button>
								</div>

								<div
									class="flex w-full flex-col items-center justify-center divide-y divide-white-dark-3 text-primary sm:items-start sm:justify-start"
								>
									<div
										class="flex w-full flex-col items-start px-4 py-6 md:px-0 md:py-11"
									>
										<!-- Below fold: hydrate on visible -->
										<LazyVisibleSectionsPropertyDescriptionSection
											hydrate-on-visible
											:commercial-id="property?.commercialId || ''"
											:summary="property?.propertySummary"
											:is-loading="isFetchingProperty"
											:ad="property?.ad"
										/>
									</div>
								</div>

								<div
									v-if="shouldShowCharacteristics"
									:id="SECTION_TYPES.CHARACTERISTICS"
									ref="characteristicsRef"
									data-section-name="Características"
									class="flex w-full flex-col gap-12 px-5 py-10 md:px-0 md:py-12"
								>
									<!-- Below fold: hydrate on visible -->
									<LazyVisibleFeaturesGrid
										v-if="shouldShowFeaturesSection"
										hydrate-on-visible
										:is-loading="isFetchingProperty"
										:features-by-category="property?.featuresByCategory || {}"
									/>

									<!-- Below fold: hydrate on visible -->
									<LazyVisibleSectionsPropertyHighlightedImagesSection
										v-if="shouldShowHighlightedImagesSection"
										hydrate-on-visible
										:images="property?.images || []"
										:is-loading="isFetchingProperty"
										:property-type="property?.propertyType?.name"
										:suites="property?.suites"
										:region="property?.region"
										:city="property?.city"
										:asking-price="property?.askingPrice"
										@open-modal="
											({
												clickType,
												photoUrl,
												ctaText
											}: {
												clickType: 'photo' | 'cta';
												photoUrl?: string;
												ctaText?: string;
											}) =>
												emit(
													'photo-modal-opened',
													'property atributes',
													clickType,
													5,
													photoUrl,
													ctaText
												)
										"
									/>
								</div>
								<div
									:id="SECTION_TYPES.LOCATION"
									ref="locationRef"
									data-section-name="Localização"
									class="w-full px-5 py-10 md:px-0 md:py-12"
								>
									<!-- Below fold: hydrate on visible -->
									<LazyVisibleSectionsLocationSection
										hydrate-on-visible
										:region="property?.region || ''"
										:city="property?.city || ''"
										:state="property?.state || ''"
										:pluscode-group="property?.pluscodeGroup || ''"
										:is-loading="isFetchingProperty"
										:show-quadrilateral="false"
										:show-marker="true"
										:variant="'primary'"
										:center-coordinates="centerCoordinates"
										:zoom="16"
										section-class="p-5 lg:p-6"
										@cta-clicked="
											emit(
												'map-modal-opened',
												'property location',
												'Ver mapa ampliado'
											)
										"
									>
										<template #markers>
											<GmapClusterRadius
												v-if="property?.h3Cluster"
												:h3-cluster="property.h3Cluster"
											/>
										</template>
										<template #address>
											<template v-if="!isFetchingProperty && property">
												<span class="text-base font-medium md:mt-1">
													{{
														`${property.propertyType.name} em ${property.region}`
													}}
												</span>
												<span class="text-sm font-normal">
													{{ `${property.city} - ${property.state}` }}
												</span>
											</template>
										</template>
										<template #another-links>
											<ClientOnly>
												<NuxtLink
													v-if="nearPropertiesLink"
													:to="nearPropertiesLink"
													class="self-start md:self-center"
													@click="
														emit('near-properties-clicked', nearPropertiesLink)
													"
												>
													<TextLink
														class="flex items-center gap-1 text-sm"
														variant="dark"
													>
														<IconMaterialSymbolsLightVillaOutline
															class="size-5 text-white"
														/>
														Imóveis próximos
													</TextLink>
												</NuxtLink>
											</ClientOnly>
										</template>
									</LazyVisibleSectionsLocationSection>
								</div>

								<div
									:id="SECTION_TYPES.CONTACT"
									ref="contactRef"
									data-section-name="Atendimento"
									class="flex w-full flex-col gap-6 px-5 py-10 md:px-0 md:py-12"
								>
									<!-- Below fold: hydrate on visible -->
									<LazyVisibleSectionsPropertyContactSection
										hydrate-on-visible
										:is-loading="isFetchingProperty"
										:agent="property?.agent || ({} as Agent)"
										:company-logo="property?.company.smallLogo || ''"
										:company-name="property?.company.name || ''"
										:commercial-id="property?.commercialId || ''"
										:region="property?.region || ''"
										:property-type="
											property?.propertyType || ({} as PropertyTypeObject)
										"
										:property-id="property?.id || ''"
										:asking-price="property?.askingPrice || 0"
										:video-shorts="(property?.featuredVideos?.length ?? 0) > 0"
										class="w-full"
										@form-property-clicked="
											({
												ctaText,
												companyName,
												agentName,
												askingPrice
											}: {
												ctaText: string;
												companyName: string;
												agentName: string;
												askingPrice: number | null;
											}) =>
												emit('form-property-clicked', {
													sectionName: 'service',
													ctaText,
													companyName,
													agentName,
													askingPrice
												})
										"
										@open-contact-modal="emit('open-contact-modal')"
									/>
								</div>

								<div
									v-if="ft_shorts_access_points"
									:id="shortsHasVideos ? SECTION_TYPES.SHORTS : undefined"
									ref="shortsSectionElRef"
									:data-section-name="shortsHasVideos ? 'Shorts' : undefined"
									class="w-full md:-mr-5 md:w-[calc(100%+20px)] lg:mr-0 lg:w-full"
								>
									<ClientOnly>
										<SectionsShortsSection
											:title="shortsTitle"
											:filters="shortsFilters"
											variant="light"
											:show-title-icon="false"
											disable-peek-from-lg
											video-card-section-name="similar shorts"
											@update:has-videos="handleShortsHasVideos"
										/>
										<template #fallback>
											<SectionsShortsSectionSkeleton
												:title="shortsTitle"
												variant="light"
											/>
										</template>
									</ClientOnly>
								</div>

								<div
									v-if="
										similarProperties?.length || isFetchingSimilarProperties
									"
									:id="SECTION_TYPES.SIMILAR_PROPERTIES"
									ref="highlightedRegionPropertiesDetailsSectionRef"
									data-section-name="Imóveis semelhantes"
									class="w-full"
								>
									<!-- Below fold: hydrate on visible -->
									<LazyVisibleSectionsHighlightedRegionPropertiesDetailsSection
										hydrate-on-visible
										:properties="similarProperties || []"
										:region="property?.region || ''"
										section="similar properties"
										:origin-property-id="property?.id"
										:title="`Imóveis semelhantes em ${property?.region}`"
										button-text="Ver todos os imóveis semelhantes"
										:button-href="similarPropertiesHref"
										:is-loading="isFetchingSimilarProperties"
										container-class="pt-10 xl:pt-12 px-0!"
										button-variant="secondary"
										title-class="px-5 md:px-0!"
										button-class="border-none hover:underline underline-offset-4 decoration-white-dark-3 px-5 md:px-0!"
										@cta-view-more-clicked="
											({
												destination,
												ctaText
											}: {
												destination: string;
												ctaText: string;
											}) =>
												emit(
													'view-more-clicked',
													'similar properties',
													destination,
													ctaText
												)
										"
									/>
								</div>
							</div>
						</div>

						<aside
							class="relative top-0 hidden w-full min-w-[330px] max-w-[330px] overflow-y-visible pb-6 lg:block lg:pt-8 xl:max-w-[400px] xl:pt-9"
						>
							<div
								ref="propertyPricesRef"
								class="sticky z-10 hidden px-5 transition-all duration-300 ease-in-out md:px-0 lg:block"
								:class="{
									'top-[181px]': navigationStore.isHeaderVisible,
									'top-[103px]': !navigationStore.isHeaderVisible
								}"
							>
								<Transition
									enter-active-class="transition-transform duration-400 ease-out"
									enter-from-class="translate-y-full"
									enter-to-class="translate-y-0"
									leave-active-class="transition-transform duration-300 ease-in"
									leave-from-class="translate-y-0"
									leave-to-class="translate-y-full"
								>
									<div
										v-show="
											property?.isExclusive && shouldShowFixedFooterExclusiveTag
										"
										class="relative z-10 flex w-full justify-center"
									>
										<div class="w-full max-w-full">
											<div
												class="flex items-center justify-center rounded-t-[2px] bg-beige px-1.5 py-1 text-center text-xs font-medium leading-[150%] text-primary"
											>
												<span class="mr-2 size-2.5">
													<GraphicsTriangle />
												</span>
												<span>Exclusivo</span>
												<span class="font-semibold">&nbsp;PilarHomes</span>
											</div>
										</div>
									</div>
								</Transition>
								<!-- Desktop only: hydrate on desktop media query -->
								<LazyMediaQueryPropertyPropertyPrices
									hydrate-on-media-query="(min-width: 1024px)"
									class="relative transition-all duration-300 ease-in-out"
									price-classes="text-primary"
									:is-exclusive="property?.isExclusive || false"
									:condo-slug="property?.canonicalCondo?.slug || ''"
									section-name="card (property details)"
									@open-contact-modal="emit('toggle-contact-modal')"
								/>
							</div>
						</aside>
					</div>
				</Container>
			</div>

			<div
				v-if="
					(chosenForYouProperties && chosenForYouProperties.length >= 5) ||
					isFetchingChosenForYouProperties
				"
				:id="SECTION_TYPES.CHOSEN_FOR_YOU"
				ref="chosenForYouRef"
				data-section-name="Imóveis semelhantes"
				class="w-full"
			>
				<Container class="size-full bg-white pt-0! pb-11 lg:pb-[60px]">
					<!-- Below fold: hydrate on visible -->
					<LazyVisibleSectionsChosenPropertiesSection
						hydrate-on-visible
						:properties="chosenForYouProperties || []"
						:is-loading="isFetchingChosenForYouProperties"
						:cta-link="chosenForYouCtaLink"
						class="w-full p-0! md:py-0!"
						container-class="justify-start! flex! items-start! mx-0! px-0! pb-0!"
						main-container-class="w-full! mx-0! flex flex-col justify-start items-start py-0"
						@cta_view_more_clicked="
							({ href, ctaText }: { href: string; ctaText: string }) =>
								emit('view-more-clicked', 'selected properties', href, ctaText)
						"
					/>
				</Container>
			</div>
			<div class="bg-white-dark-1">
				<Container class="py-6! md:py-8!">
					<div
						data-section-name="Visão geral"
						class="flex w-fit items-center justify-start px-5 md:justify-start md:px-0"
					>
						<!-- At the bottom of page: hydrate on visible -->
						<LazyVisibleBreadcrumb
							hydrate-on-visible
							:items="breadcrumbItems"
							:is-loading="isFetchingProperty"
							@breadcrumb-clicked="
								(event: BreadcrumbItemWithIndex) =>
									emit('breadcrumb-clicked', event)
							"
						/>
						<CopyButton @click-copy="emit('copy-commercial-id')" />
					</div>
				</Container>
			</div>
		</section>
		<LazySimilarPropertiesFloatingCtaComponent
			class="fixed z-40 lg:bottom-8"
			:class="[property?.isExclusive ? 'bottom-[107px]' : 'bottom-[81px]']"
			:show="shouldShowFloatingCTA"
			@click="handleFloatingCtaClick"
		/>
	</div>
</template>
